import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ValidationError, SchemaEntry, ValidationProviderConfig, ValidationContextValue } from './types';
import { ValidationContext } from './ValidationContext';

interface ValidationProviderProps {
  showToasts: boolean;
  showSuccessFeedback: boolean;
  schemaMap: Record<string, SchemaEntry>;
  children: React.ReactNode;
}

export function ValidationProvider({
  showToasts,
  showSuccessFeedback,
  schemaMap,
  children,
}: ValidationProviderProps) {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [blurredFields, setBlurredFields] = useState<Set<string>>(new Set());
  const [shakeKey, setShakeKey] = useState(0);

  // Stable ref to current errors — used in callbacks that shouldn't re-create on every error change
  const errorsRef = useRef<ValidationError[]>(errors);
  errorsRef.current = errors;

  // Re-validate all scopes whenever schemaMap data references change.
  // schemaMap itself is the dependency — callers must stabilize it (e.g. useMemo) if they
  // want to avoid unnecessary re-runs, which is the correct React contract.
  useEffect(() => {
    const nextErrors: ValidationError[] = [];
    for (const [scope, entry] of Object.entries(schemaMap)) {
      const result = entry.schema.safeParse(entry.data);
      if (!result.success) {
        for (const issue of result.error.issues) {
          nextErrors.push({ field: issue.path.join('.'), message: issue.message, scope });
        }
      }
    }
    setErrors(nextErrors);
  }, [schemaMap]);

  const runScope = useCallback(
    (scope: string | number): ValidationError[] => {
      const entry = schemaMap[scope];
      if (!entry) return [];
      const result = entry.schema.safeParse(entry.data);
      if (result.success) return [];
      return result.error.issues.map((issue) => ({
        field: issue.path.join('.'),
        message: issue.message,
        scope,
      }));
    },
    [schemaMap]
  );

  const validate = useCallback(
    (scope?: string | number): ValidationError[] => {
      if (scope === undefined) return [];
      const scopeErrors = runScope(scope);
      setErrors((prev) => {
        const withoutScope = prev.filter((e) => e.scope !== scope);
        return [...withoutScope, ...scopeErrors];
      });
      return scopeErrors;
    },
    [runScope]
  );

  const validateAll = useCallback((): ValidationError[] => {
    const allErrors: ValidationError[] = [];
    for (const scope of Object.keys(schemaMap)) {
      allErrors.push(...runScope(scope));
    }
    setErrors(allErrors);
    return allErrors;
  }, [schemaMap, runScope]);

  const getFieldError = useCallback(
    (fieldKey: string): string | null => {
      if (!blurredFields.has(fieldKey)) return null;
      const error = errors.find((e) => e.field === fieldKey);
      return error ? error.message : null;
    },
    [errors, blurredFields]
  );

  const markFieldBlurred = useCallback(
    (fieldKey: string): void => {
      setBlurredFields((prev) => {
        const next = new Set(prev);
        next.add(fieldKey);
        return next;
      });
      // Re-validate all scopes so errors appear immediately after blur
      const allErrors: ValidationError[] = [];
      for (const scope of Object.keys(schemaMap)) {
        allErrors.push(...runScope(scope));
      }
      setErrors(allErrors);
    },
    [schemaMap, runScope]
  );

  const isValid = useCallback(
    (scope?: string | number): boolean => {
      if (scope === undefined) return errors.length === 0;
      return !errors.some((e) => e.scope === scope);
    },
    [errors]
  );

  const getActiveErrors = useCallback(
    (scope?: string | number): ValidationError[] => {
      if (scope === undefined) return errors;
      return errors.filter((e) => e.scope === scope);
    },
    [errors]
  );

  const clearErrors = useCallback((): void => {
    setErrors([]);
    setBlurredFields(new Set());
  }, []);

  const triggerShake = useCallback((): void => {
    setShakeKey((k) => k + 1);
    // Mark all current error fields as blurred so inline errors appear.
    // Read from ref to avoid recreating this callback on every error change.
    const currentErrors = errorsRef.current;
    setBlurredFields((prev) => {
      const next = new Set(prev);
      for (const e of currentErrors) {
        next.add(e.field);
      }
      return next;
    });
  }, []);

  const config: ValidationProviderConfig = useMemo(
    () => ({ showToasts, showSuccessFeedback, schemaMap }),
    [showToasts, showSuccessFeedback, schemaMap]
  );

  const contextValue: ValidationContextValue = useMemo(
    () => ({
      getFieldError,
      markFieldBlurred,
      getActiveErrors,
      isValid,
      validate,
      validateAll,
      clearErrors,
      triggerShake,
      shakeKey,
      config,
    }),
    [
      getFieldError,
      markFieldBlurred,
      getActiveErrors,
      isValid,
      validate,
      validateAll,
      clearErrors,
      triggerShake,
      shakeKey,
      config,
    ]
  );

  return (
    <ValidationContext.Provider value={contextValue}>
      {children}
    </ValidationContext.Provider>
  );
}
